import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { startOfWeek } from 'date-fns'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { Calendar } from '../ui/calendar'
import type { CalendarDaySummary } from '../../services/training-diary'

interface MonthPickerSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDaySelect: (date: Date) => void
  getCalendarSummary: (from: string, to: string) => Promise<Record<string, CalendarDaySummary>>
}

function DotIndicator({ session, attempts, goal }: CalendarDaySummary) {
  if (!session && !attempts && !goal) return null
  return (
    <div className="flex gap-0.5 justify-center mt-0.5">
      {(session || attempts) && <span className="size-1.5 rounded-full bg-blue-400" />}
      {goal && <span className="size-1.5 rounded-full bg-orange-400" />}
    </div>
  )
}

export function MonthPickerSheet({ open, onOpenChange, onDaySelect, getCalendarSummary }: MonthPickerSheetProps) {
  const { t } = useTranslation()
  const [summary, setSummary] = useState<Record<string, CalendarDaySummary>>({})
  const [month, setMonth] = useState(new Date())

  useEffect(() => {
    if (!open) return
    const from = new Date(month.getFullYear(), month.getMonth(), 1)
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0)
    const fromStr = from.toISOString().slice(0, 10)
    const toStr = to.toISOString().slice(0, 10)
    getCalendarSummary(fromStr, toStr).then(setSummary).catch(() => {})
  }, [open, month, getCalendarSummary])

  function handleDayClick(day: Date | undefined) {
    if (!day) return
    const weekStart = startOfWeek(day, { weekStartsOn: 1 })
    onDaySelect(weekStart)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle className="sr-only">{t('trainingDiary.pickWeek')}</SheetTitle>
        </SheetHeader>
        <div className="flex justify-center pt-4">
          <Calendar
            mode="single"
            month={month}
            onMonthChange={setMonth}
            onDayClick={handleDayClick}
            components={{
              DayButton: ({ day, ...props }) => {
                const dateStr = day.date.toISOString().slice(0, 10)
                const info = summary[dateStr]
                return (
                  <button
                    {...props}
                    className="flex flex-col items-center justify-center size-[--cell-size] rounded-md hover:bg-accent text-sm"
                  >
                    <span>{day.date.getDate()}</span>
                    {info && <DotIndicator {...info} />}
                  </button>
                )
              },
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
